import { useState } from "react";
import axios from "axios";
import { TFunction } from "i18next";
import { Ingredient } from "../types/Types";
import { downloadIngredientsExcel, IngredientExportFilters } from "../utils/osExcel";

interface UseRNIngredientExcelExportArgs {
  ingredient: Ingredient | null;
  selectedItemCode: string;
  language: string;
  showToast: (message: string, type: "success" | "error") => void;
  t: TFunction;
}

// Excel export (сите / тековен / филтрирани) за Основна Спецификација од РН екранот.
// Move-only out of RNWorkOrder.tsx — same state, same behavior.
export const useRNIngredientExcelExport = ({
  ingredient,
  selectedItemCode,
  language,
  showToast,
  t,
}: UseRNIngredientExcelExportArgs) => {
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      await downloadIngredientsExcel(language);
      showToast(t('messages.excelDownloadSuccess') || 'Excel downloaded', 'success');
    } catch {
      showToast(t('errors.excelDownloadFailed') || 'Export failed', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCurrent = async () => {
    if (!ingredient?._id) return;
    setIsExporting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const token = localStorage.getItem('token');
      const lang = (language || 'mk').split('-')[0];
      const response = await axios.get(`${apiUrl}/api/materialIngredients/export/excel`, {
        headers: { 'x-auth-token': token || '' },
        params: { ingredientId: ingredient._id, lang },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `material_ingredients_${selectedItemCode || ingredient._id}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast(t('messages.excelDownloadSuccess') || 'Excel downloaded', 'success');
    } catch {
      showToast(t('errors.excelDownloadFailed') || 'Export failed', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportFiltered = async (filters: IngredientExportFilters) => {
    setIsExporting(true);
    try {
      await downloadIngredientsExcel(language, filters);
      showToast(t('messages.excelDownloadSuccess') || 'Excel downloaded', 'success');
      setShowExportModal(false);
    } catch {
      showToast(t('errors.excelDownloadFailed') || 'Export failed', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return {
    showExportModal,
    setShowExportModal,
    isExporting,
    handleExportAll,
    handleExportCurrent,
    handleExportFiltered,
  };
};
